/**
 * Passkey (WebAuthn) login for the native technician app.
 *
 * Replaces password-in-keystore quick login with a phishing-resistant platform
 * passkey (Android Credential Manager / iOS AuthenticationServices via the
 * WebAuthn API exposed in the Capacitor WebView).
 *
 * Only the normalized phone number is stored locally (non-secret UX hint).
 * Legacy password credentials are removed on successful passkey enrollment.
 */
import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import apiClient from '@/api/client'
import { isNative } from './platform'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import { configureNativeSecureStorage, nativeSecureSetString } from './secureStorageConfig'
import { secureGet, secureRemove } from './secureStorage'
import { deleteCredentials } from './biometric'
import { markBiometricLoginConfigured } from './biometricEnrollment'

export const PASSKEY_PHONE_KEY = 'passkey_phone'

function _storage() {
  if (!isNative()) return null
  return SecureStorage || null
}

/**
 * @returns {Promise<boolean>}
 */
export function canUsePasskey() {
  return isNative() && typeof PublicKeyCredential !== 'undefined'
}

/**
 * @returns {Promise<string|null>}
 */
export async function getPasskeyPhone() {
  if (!isNative()) return null
  try {
    await configureNativeSecureStorage()
    const phone = await secureGet(PASSKEY_PHONE_KEY)
    if (phone == null || phone === '') return null
    const normalized = String(phone).trim()
    return normalized || null
  } catch {
    return null
  }
}

/**
 * @param {string} phone normalized E.164 phone
 */
export async function savePasskeyPhone(phone) {
  if (!isNative() || !phone) return
  try {
    await configureNativeSecureStorage()
    await nativeSecureSetString(PASSKEY_PHONE_KEY, phone)
    markBiometricLoginConfigured(phone)
  } catch (e) {
    console.warn('[Passkey] savePasskeyPhone failed:', e?.message || e)
  }
}

export async function clearPasskeyLocal() {
  if (!isNative()) return
  try { await secureRemove(PASSKEY_PHONE_KEY) } catch { /* ignore */ }
}

/**
 * Check server-side whether this phone has a registered passkey.
 * @param {string} phone
 * @returns {Promise<boolean>}
 */
export async function hasServerPasskey(phone) {
  if (!phone) return false
  try {
    const { data } = await apiClient.get('/auth/passkey/status/', {
      params: { phone_number: phone },
    })
    return !!data?.has_passkey
  } catch {
    return false
  }
}

/**
 * Register a platform passkey after a successful password login.
 * @param {string} [deviceName]
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function registerPasskey(deviceName) {
  if (!canUsePasskey()) {
    return { ok: false, error: 'passkey-unavailable' }
  }
  try {
    const { data: optData } = await apiClient.post('/auth/passkey/register/options/')
    const { options, challenge_token: challengeToken } = optData
    if (!options || !challengeToken) {
      return { ok: false, error: 'invalid-options' }
    }

    const credential = await startRegistration({ optionsJSON: options })
    await apiClient.post('/auth/passkey/register/verify/', {
      challenge_token: challengeToken,
      credential,
      device_name: deviceName || navigator.userAgent || 'mobile',
    })

    // Drop legacy password-in-keystore if present.
    await deleteCredentials()
    return { ok: true }
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || String(e)
    console.warn('[Passkey] registerPasskey failed:', msg)
    return { ok: false, error: msg }
  }
}

/**
 * Sign in with a passkey (returns auth payload or multi-domain selection).
 * Passkey proves the user only — company is chosen separately when needed.
 * @param {string} phone normalized E.164 phone
 * @param {string} [domainCode]
 * @returns {Promise<{ok: boolean, data?: object, error?: string}>}
 */
export async function loginWithPasskey(phone, domainCode = null) {
  if (!canUsePasskey()) {
    return { ok: false, error: 'passkey-unavailable' }
  }
  if (!phone) {
    return { ok: false, error: 'missing-phone' }
  }
  try {
    const { data: optData } = await apiClient.post('/auth/passkey/login/options/', {
      phone_number: phone,
    })
    const { options, challenge_token: challengeToken } = optData
    if (!options || !challengeToken) {
      return { ok: false, error: 'invalid-options' }
    }

    const credential = await startAuthentication({ optionsJSON: options })
    const body = {
      phone_number: phone,
      challenge_token: challengeToken,
      credential,
    }
    if (domainCode) body.domain_code = domainCode

    const { data } = await apiClient.post('/auth/passkey/login/verify/', body)

    if (data?.error) {
      return { ok: false, error: data.error }
    }

    await savePasskeyPhone(phone)
    return { ok: true, data }
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || String(e)
    console.warn('[Passkey] loginWithPasskey failed:', msg)
    return { ok: false, error: msg }
  }
}

/**
 * Complete passkey login after the user picked a company (no Face ID re-prompt).
 * @param {string} phone
 * @param {string} passkeySessionToken
 * @param {string} domainCode
 * @returns {Promise<{ok: boolean, data?: object, error?: string}>}
 */
export async function completePasskeyDomainLogin(phone, passkeySessionToken, domainCode) {
  if (!phone || !passkeySessionToken || !domainCode) {
    return { ok: false, error: 'missing-params' }
  }
  try {
    const { data } = await apiClient.post('/auth/passkey/login/verify/', {
      phone_number: phone,
      passkey_session_token: passkeySessionToken,
      domain_code: domainCode,
    })
    if (data?.error) {
      return { ok: false, error: data.error }
    }
    await savePasskeyPhone(phone)
    return { ok: true, data }
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || String(e)
    console.warn('[Passkey] completePasskeyDomainLogin failed:', msg)
    return { ok: false, error: msg }
  }
}

/**
 * Whether quick passkey login should be offered on the login screen.
 * Requires a LOCAL passkey phone hint (this device enrolled) AND a server
 * credential. Server-only passkeys (other device / reinstall) must NOT count
 * as ready — that skipped the Face ID enroll dialog on iOS.
 * @param {string} [phone]
 * @returns {Promise<boolean>}
 */
export async function isPasskeyLoginAvailable(phone) {
  if (!canUsePasskey()) return false
  const localPhone = await getPasskeyPhone()
  if (!localPhone) return false
  if (phone) {
    const a = String(phone).replace(/\D/g, '')
    const b = String(localPhone).replace(/\D/g, '')
    if (a && b && a !== b) return false
  }
  return hasServerPasskey(phone || localPhone)
}
